import { runInAction, action, observable, computed } from "mobx";
import { IStoreLifeCycle } from "@interface";

import { LoginErrorResponseModel } from "@data-types";
import { UserAccount } from "@data-types/user";

import {
	LoginHistoryResponseModel,
	ActivationTypeEnum as AccountActivationTypeEnum,
	LoginResponseDto,
	LoginResponseUser,
} from "@api-types/user/LoginResponseDto";
import {
	LoginRequestDto,
	LoginSSORequestDto,
} from "@api-types/user/LoginRequestDto";

import { getCurrentCulture } from "@utils";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { LoginType } from "@utils/common/loginTypeEnum";
import { MitAuthDto } from "@api-types/integrations/MitAuthDto";
import { StorageStateKeysEnum } from "@utils/storage";
import {
	getRedirectOriginUrl,
	isCapacitorPlatform,
} from "@utils/specific/capacitor/helpers";
import { LoaderStore } from "@state/stores/common";
import { BankIdAuthDto } from "@api-types/integrations";
import BlockUserActionService from "@services/user/BlockUserActionService";
import { UseFormReturn } from "react-hook-form";
import { LoginHash } from "@api-types/integrations/MitIdWidgetDto";
import { LoginErrorCodes } from "@data-types/user/LoginErrorCodes";
import { BankIdLoginErrorCodes } from "@data-types";
import {
	isBankIdRedirectCancel,
	hasBankIdRedirectFailure,
	bankIdRedirectMessageKey,
	clearBankIdRedirectParams,
} from "@utils/specific/bankid/bankIdRedirect";
import { RequiredUserAction, UserData } from "./types";
import { analyticsTracker } from "@utils/analytics";
import { UserAuthStore, UserToken } from "@state/shared";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const PersonalDataService = lazy(loadFailPath, ()=>import("@services/account-settings/PersonalDataService"));
// prettier-ignore
const UserService = lazy(loadFailPath, ()=>import("@services/user/UserService"))

class LoginStore implements IStoreLifeCycle {
	userAuthStore: UserAuthStore;
	formContext?: UseFormReturn<LoginRequestDto>;

	@observable isLoginWorking: boolean = false;
	@observable isStoreInitialized: boolean = false;
	@observable loginError: LoginErrorResponseModel | null = null;
	@observable userMatchingDataList: string[] = [];
	@observable isUserMatchingInProgress: boolean = false;
	@observable isDataConfirmationInProgress: boolean = false;
	@observable loader: LoaderStore;
	@observable shouldShowPageRefreshPopup: boolean = false;

	/**
	 * In case of sso login (redirect with params) language service triggers rerender:
	 * - resulting in useEffects triggering twice
	 * flag is set to true on first render, value is checked on entering use effect to make sure we trigger login only once
	 */
	isSSOLoginInitialized: boolean = false;

	@computed public get isLoading() {
		return this.loader.isLoading;
	}

	@computed public get nextRequiredUserAction(): RequiredUserAction {
		if (
			this.isLoginWorking &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.IsLoginWorking
			)
		) {
			return RequiredUserAction.IsLoginWorking;
		}

		if (
			this.userAuthStore.accountActivation?.activationType ===
				AccountActivationTypeEnum.AccountActivation &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.AccountActivation
			)
		) {
			return RequiredUserAction.AccountActivation;
		}

		if (
			this.userAuthStore.accountActivationStore
				.isAccountActivationCongratulationsActive &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.AccActivationCongratulation
			)
		) {
			return RequiredUserAction.AccActivationCongratulation;
		}

		if (
			this.isDataConfirmationInProgress &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.ConfirmUserData
			)
		) {
			return RequiredUserAction.ConfirmUserData;
		}

		if (
			this.userAuthStore.accountActivation?.activationType ===
				AccountActivationTypeEnum.PasswordChange &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.PasswordChange
			)
		) {
			return RequiredUserAction.PasswordChange;
		}

		if (
			this.isUserMatchingInProgress &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.UserMatching
			)
		) {
			return RequiredUserAction.UserMatching;
		}

		if (
			this.userAuthStore.user?.bettingActivityNeeded &&
			(this.userAuthStore.bettingActivityStore.bettingActivityFetchedData
				?.bettingActivity != null ||
				this.userAuthStore.bettingActivityStore
					.bettingActivityFetchedData?.userActivity != null) &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.BettingActivity
			)
		) {
			return RequiredUserAction.BettingActivity;
		}

		if (
			this.userAuthStore.user?.bettingActivityNeeded &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.BettingActivity
			)
		) {
			return RequiredUserAction.IsLoginWorking;
		}

		if (
			this.userAuthStore.user?.shouldAcceptTermsAndConditions &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.AcceptNewTerms
			)
		) {
			return RequiredUserAction.AcceptNewTerms;
		}

		if (
			this.userAuthStore.isPartiallyLoggedIn &&
			!this.userAuthStore.isLoggedIn
		) {
			return RequiredUserAction.IsLoginWorking;
		}

		if (
			this.userAuthStore.user?.shouldAcceptGdpr &&
			!isCapacitorPlatform() &&
			!BlockUserActionService.checkIfActionBlocked(
				RequiredUserAction.AcceptGdpr
			)
		) {
			//currently disabled for capacitor platform
			return RequiredUserAction.AcceptGdpr;
		}

		return RequiredUserAction.None;
	}

	get userMatchingInfoMessage() {
		return this.userMatchingDataList != null &&
			this.userMatchingDataList.length !== 0
			? "USER_MATCHING.MANUAL_MATCH_DATA_AVAILABLE"
			: "USER_MATCHING.MANUAL_MATCH_NO_DATA";
	}

	get pathname() {
		return window.location.pathname[0] == "/"
			? window.location.pathname.slice(1)
			: window.location.pathname;
	}

	get redirectUrl() {
		return getRedirectOriginUrl(this.pathname, true);
	}

	//#region constructor
	constructor(userAuthStore: UserAuthStore) {
		this.userAuthStore = userAuthStore;
		this.loader = new LoaderStore();
	}

	@action.bound
	handleSsoLogin(
		params: Record<string, string>,
		formContext?: UseFormReturn<LoginRequestDto>
	) {
		this.formContext = formContext;

		if (this.isSSOLoginInitialized) {
			return;
		}

		const { sessionId, code, token, error } = params;

		const cancel = isBankIdRedirectCancel();
		const failureWithoutSession = hasBankIdRedirectFailure() && !sessionId;
		if (WEBPACK_IS_BANKID_ENABLED && (cancel || failureWithoutSession)) {
			this.isSSOLoginInitialized = true;

			this.loginError = {
				errorCode: String(
					cancel
						? BankIdLoginErrorCodes.authFailedOrNotCompleted
						: BankIdLoginErrorCodes.authResponseNotValid
				),
				message: bankIdRedirectMessageKey(cancel),
			};

			this.checkLoginErrors();
			clearBankIdRedirectParams();
			return;
		}

		if (error) {
			this.userAuthStore.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.MITID_LOGIN_HASH
			);
			return;
		}

		// sso auth
		if (token) {
			this.isSSOLoginInitialized = true;

			const data: LoginSSORequestDto = { token };
			this.handleLoginSubmit(data, LoginType.SSO);
			return;
		}

		// bank auth. ignore 'sessionId' param if BankID integration is disabled
		if (sessionId && WEBPACK_IS_BANKID_ENABLED) {
			this.isSSOLoginInitialized = true;

			const data: BankIdAuthDto = { sessionId };
			this.handleLoginSubmit(data, LoginType.BANKID);
			return;
		}

		// mit auth
		if (code) {
			this.isSSOLoginInitialized = true;

			const { state, iss } = params;

			const loginHash = JSON.parse(
				this.userAuthStore.rootStore.localStorageProvider.get(
					StorageStateKeysEnum.MITID_LOGIN_HASH
				) as string
			) as LoginHash;
			if (loginHash?.state == null || loginHash?.nonce == null) {
				this.loginError = {
					errorCode: "5555",
					message: "MIT_MISSING_ORIGINAL_STATE",
				};
				this.checkLoginErrors();
				return;
			}

			const mitData: MitAuthDto = {
				code,
				state,
				iss,
				originalState: loginHash.state,
				originalNonce: loginHash.nonce,
				redirectUrl: this.redirectUrl,
			};

			this.handleLoginSubmit(mitData, LoginType.MITID);
		}
	}

	@action.bound
	checkLoginErrors() {
		if (
			this.loginError != null &&
			typeof this.loginError.userData !== "string" &&
			this.loginError.userData?.activationToken == null
		) {
			this.loader.resume();
			this.formContext && this.formContext.resetField("password");
		}
	}

	@action.bound
	async handleLoginSubmit(
		data: LoginRequestDto | LoginSSORequestDto | BankIdAuthDto | MitAuthDto,
		loginType: LoginType = LoginType.DEFAULT
	) {
		this.loader.suspend();
		this.resetLoginErrorState();

		if (loginType == LoginType.MITID) {
			this.userAuthStore.rootStore.localStorageProvider.remove(
				StorageStateKeysEnum.MITID_LOGIN_HASH
			);
		}

		await this.onLoginSubmit(data, loginType);
	}

	@action.bound
	public onInitialize(): void {
		this.readFromLocalStorage(null);
		window.addEventListener("storage", this.storageActionListener);
	}

	@action.bound
	public onInitializeLogin(): void {
		this.onInitialize();

		this.userAuthStore.removeUserFromLocalStorage();

		this.userAuthStore.rootStore.automaticLogoutStore.clearTimer();
		this.userAuthStore.rootStore.realityCheckStore.clearTimer();
		this.userAuthStore.rootStore.closePopups();

		//remove authorization cookies

		document.cookie = `authorization=;expires=${new Date().toUTCString()};path=/`;
		document.cookie = `authorization_token=;expires=${new Date().toUTCString()};path=/`;

		this.isStoreInitialized = true;
	}

	@action.bound
	public async onLoginSubmit(
		loginData:
			| LoginRequestDto
			| LoginSSORequestDto
			| MitAuthDto
			| BankIdAuthDto,
		loginType: LoginType = LoginType.DEFAULT
	): Promise<void> {
		this.isLoginWorking = true;
		try {
			const response = await (
				await UserService
			).default.loginUser(loginData, loginType);

			if (response != null) {
				const sln = (loginData as LoginRequestDto).stayLoggedIn;

				if (sln) {
					this.userAuthStore.rootStore.localStorageProvider.set(
						StorageStateKeysEnum.SLN,
						`${sln}`
					);
				}

				this.userAuthStore.login(response);

				analyticsTracker.trackLoginSuccess(response.primary.user);

				if (response.primary.loginHistory == null) {
					analyticsTracker.trackFirstLogin();
				}

				if (
					Number(response.errorCode) ===
					LoginErrorCodes.dataConfirmationRequiredCode
				) {
					this.setUserDataConfirmationState(true);
					await this.resolveUserDataConfirmationData(response);
				}

				if (
					Number(response.errorCode) ===
					LoginErrorCodes.matchingAvailable
				) {
					this.userAuthStore.userMatchingStore.setUserMatchingState(
						true
					);

					await this.userAuthStore.userMatchingStore.fetchUserMatchingData();
				}
			}
		} catch (error) {
			if (error.errorCode === 400259285) {
				this.shouldShowPageRefreshPopup = true;
				return;
			}

			if (error.errorCode == 400272) {
				this.loginError = {
					errorCode: error.errorCode,
					message: "MIT_UNSUPPORTED_GRANT_TYPE", //same error message for bankId
				};
				return;
			}

			if (error?.userData) {
				if (error.userData?.activationToken) {
					//show page with user data
					this.userAuthStore.userDataConfirmationStore.setUserDataToBeConfirmed(
						{
							...error.userData,
							isInternetUser: false,
						}
					);
					this.setUserDataConfirmationState(true);
					this.userAuthStore.accountActivationStore.setUserConfirmationData(
						error.userData
					);
					this.resetLoginErrorState();
				} else {
					//show message about shop
					runInAction(() => {
						this.loginError = {
							errorCode: error.errorCode,
							message: error.userData.shopInfo,
						};
					});
				}
			} else {
				runInAction(() => (this.loginError = error));
			}
		} finally {
			runInAction(() => {
				this.isLoginWorking = false;
			});
			this.checkLoginErrors();
		}
	}

	@action.bound
	resetLoginErrorState(resetLoader?: boolean) {
		this.loginError = null;
		resetLoader && this.loader.resume();
	}

	@action.bound
	public setUserDataConfirmationState(
		isDataConfirmationInProgress: boolean
	): void {
		this.userAuthStore.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.UDC,
			isDataConfirmationInProgress
		);

		this.userAuthStore.loginStore.isDataConfirmationInProgress =
			isDataConfirmationInProgress;
	}

	@action.bound
	public getDataConfirmationState() {
		this.userAuthStore.loginStore.isDataConfirmationInProgress =
			this.userAuthStore.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.UDC
			) as boolean;
	}

	@action.bound
	public async resolveUserDataConfirmationData(
		response: LoginResponseDto
	): Promise<void> {
		const fetchedUserData = await (
			await PersonalDataService
		).default.getPersonalData(false);
		if (fetchedUserData != null && fetchedUserData.data != null) {
			const userDataModel = fetchedUserData.data;
			const userData: UserData = {
				activationToken:
					response.primary.accountActivation?.activationToken,
				agencyIdentifier: response.primary.user.agencyId,
				birthPlace: userDataModel.birthPlace,
				city: userDataModel.city,
				countryId: userDataModel.countryId!,
				firstName: userDataModel.firstName,
				houseNumber: userDataModel.houseNumber,
				lastName: userDataModel.lastName,
				shopId: response.primary.user.shopId,
				shopInfo: null,
				street: userDataModel.street,
				userName: userDataModel.userName,
				zipCode: userDataModel.zipCode,
				isInternetUser: response.primary.user.isInternetUser,
				currentEmail: "",
				dob: userDataModel.dob,
				language: userDataModel.language,
				mobilePhone: userDataModel.mobilePhone,
				payPin: userDataModel.payPin,
				receiveNewsletter: userDataModel.receiveNewsletter,
				telephoneNumber: userDataModel.telephoneNumber,
				titleId: userDataModel.titleId,
				occupation: userDataModel.occupation,
				nationality: userDataModel.nationality,
				birthName: userDataModel.birthName,
				documentExpiration: userDataModel.documentExpiration,
				regionId: userDataModel.regionId ?? null,
			};
			this.userAuthStore.userDataConfirmationStore.setUserDataToBeConfirmed(
				userData
			);
		}
	}

	@action.bound
	async resolveUserActivationLogin(
		primary: {
			user: LoginResponseUser;
			token: UserToken;
			userAccounts: UserAccount[];
			loginHistory: LoginHistoryResponseModel;
		},
		secondary?: {
			user: LoginResponseUser;
			token: UserToken;
		},
		errorCode?: number
	): Promise<void> {
		this.isLoginWorking = true;
		this.userAuthStore.login({
			primary,
			secondary,
		});

		if (errorCode === LoginErrorCodes.dataConfirmationRequiredCode) {
			this.setUserDataConfirmationState(true);
			await this.resolveUserDataConfirmationData({
				//@ts-expect-error
				primary: primary,
				//@ts-expect-error
				secondary: secondary,
			});
		}

		if (errorCode === LoginErrorCodes.matchingAvailable) {
			await this.userAuthStore.userMatchingStore.fetchUserMatchingData();
		}

		runInAction(() => {
			this.isLoginWorking = false;
		});
	}

	@action.bound
	private storageActionListener = (e: StorageEvent) => {
		this.readFromLocalStorage(e.key);
	};

	@action.bound
	private readFromLocalStorage(key: string | null) {
		if (key == null || key == "user_matching") {
			this.userAuthStore.userMatchingStore.setUserMatchingState(
				Boolean(
					this.userAuthStore.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.USER_MATCHING
					)
				)
			);
		}

		if (key == null || key == "udc") {
			this.setUserDataConfirmationState(
				Boolean(
					this.userAuthStore.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.UDC
					)
				)
			);
		}
		if (key == null || key == "user-data-to-be-confirmed") {
			this.userAuthStore.userDataConfirmationStore.setUserDataToBeConfirmed(
				JSON.parse(
					this.userAuthStore.rootStore.localStorageProvider.get(
						StorageStateKeysEnum.USER_DATA_TO_BE_CONFIRMED
					) as string
				)
			);
		}
	}

	@action.bound
	public onDispose(): void {
		window.removeEventListener("storage", this.storageActionListener);
		this.userAuthStore.userDataConfirmationStore.onDisposeUserDataConfirmation();
		this.userAuthStore.accountActivationStore.onDisposeAccountActivation();
		this.isStoreInitialized = false;
		this.isSSOLoginInitialized = false;
	}
}

export default LoginStore;
